// Tool-mode fields split out of connectorConfig.js to keep that file under the 300-line ceiling.
// connectorConfig.js imports from here, so this module stays self-contained and does not import
// back from it (a URL string travels in from the caller instead of a shared placeholder helper).
export const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]{2,40}$/;
export const ARG_NAME_PATTERN = /^[a-z][a-z0-9_]{0,30}$/;
export const ARG_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'enum', label: 'Enum' },
];
export const MAX_ARGS = 6;
export const MAX_ROWS_BOUNDS = { min: 1, max: 50 };
export const TEXT_CHARS_BOUNDS = { min: 300, max: 3000 };
export const CAPS = { description: 300, guidance: 1000, notFoundText: 200, statusText: 40, argMaxLength: 300 };

// Keys on the name and description, not on a pattern: a 12-digit product number such as an
// `item_code` argument has to pass, and the digit shape alone says nothing about what it identifies.
const IDENTIFIER_LINT = /(e-?mail|phone|customer|user|profile|account|\bid\b|_id$|Id$)/i;

const inBounds = (value, bounds) => Number.isInteger(value) && value >= bounds.min && value <= bounds.max;

export const emptyArg = () => ({
  name: '',
  type: 'string',
  description: '',
  required: false,
  enumValues: '',
  pattern: '',
  maxLength: '',
});

export const emptyTool = () => ({
  name: '',
  description: '',
  argRows: [],
  guidance: '',
  notFoundText: '',
  personal: false,
  maxRows: 10,
  maxTextChars: '',
  statusText: '',
});

const argToRow = (arg) => ({
  name: arg.name || '',
  type: arg.type || 'string',
  description: arg.description || '',
  required: !!arg.required,
  enumValues: (arg.enum || []).join(', '),
  pattern: arg.pattern || '',
  maxLength: arg.maxLength === undefined ? '' : String(arg.maxLength),
});

export const toolToForm = (tool) => {
  if (!tool) return emptyTool();

  return {
    name: tool.name || '',
    description: tool.description || '',
    argRows: (tool.args || []).map(argToRow),
    guidance: tool.guidance || '',
    notFoundText: tool.notFoundText || '',
    personal: !!tool.personal,
    maxRows: tool.maxRows ?? 10,
    maxTextChars: tool.maxTextChars ?? '',
    statusText: tool.statusText ?? '',
  };
};

const rowToArg = (row) => {
  const arg = { name: row.name.trim(), type: row.type, description: row.description.trim(), required: !!row.required };

  if (row.type === 'enum') {
    const values = row.enumValues
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length) arg.enum = values;
  } else if (row.pattern.trim()) {
    arg.pattern = row.pattern.trim();
  }

  if (row.maxLength !== '') arg.maxLength = Number(row.maxLength);

  return arg;
};

export const toolToBody = (toolForm) => ({
  name: toolForm.name.trim(),
  description: toolForm.description.trim(),
  args: toolForm.argRows.map(rowToArg),
  guidance: toolForm.guidance.trim(),
  notFoundText: toolForm.notFoundText.trim(),
  personal: !!toolForm.personal,
  maxRows: toolForm.maxRows,
  // Blank leaves the key off, so the server keeps its default of 300.
  ...(toolForm.maxTextChars === '' ? {} : { maxTextChars: toolForm.maxTextChars }),
  // Blank leaves the key off, so the shopper sees the server's generic status.
  ...(toolForm.statusText.trim() ? { statusText: toolForm.statusText.trim() } : {}),
});

// Each arm reports on the box the offending text is actually in: rowToArg drops the pattern of an
// enum row, and the workspace only renders the Pattern input for a non-enum row, so a
// pattern-keyed message on an enum would block Save with nothing on screen to fix.
const IDENTITY_SHAPE_MESSAGE = 'An email shape is only allowed on a personal tool.';
const IDENTIFIER_MESSAGE = 'Reads as a shopper identifier; only a personal tool may ask for one.';

const patternAdmitsEmailShape = (row) => row.type !== 'enum' && !!row.pattern && row.pattern.includes('@');
const enumAdmitsEmailShape = (row) => row.type === 'enum' && row.enumValues.includes('@');

const oneArgErrors = (row, personal) => {
  const errors = {};
  const name = row.name.trim();

  if (!ARG_NAME_PATTERN.test(name))
    errors.name = 'Lowercase letters, digits and underscores, starting with a letter, up to 31 characters.';

  if (!row.description.trim()) errors.description = 'A description is required.';
  else if (row.description.length > CAPS.description) errors.description = `At most ${CAPS.description} characters.`;

  if (row.maxLength !== '') {
    const value = Number(row.maxLength);
    if (!Number.isInteger(value) || value < 1 || value > CAPS.argMaxLength)
      errors.maxLength = `Between 1 and ${CAPS.argMaxLength}.`;
  }

  // A personal tool is allowed to key on a shopper's identity; a non-personal one is not.
  if (!personal) {
    if (IDENTIFIER_LINT.test(row.name)) errors.name = errors.name || IDENTIFIER_MESSAGE;
    else if (IDENTIFIER_LINT.test(row.description)) errors.description = errors.description || IDENTIFIER_MESSAGE;
    if (patternAdmitsEmailShape(row)) errors.pattern = errors.pattern || IDENTITY_SHAPE_MESSAGE;
    if (enumAdmitsEmailShape(row)) errors.enumValues = errors.enumValues || IDENTITY_SHAPE_MESSAGE;
  }

  return errors;
};

// No `{{` before the first `/` after `://`: a placeholder there would let the model choose the
// host or port, and the store's secret in a query parameter would follow it there.
export const authorityError = (url) => {
  const text = String(url ?? '');
  const schemeEnd = text.indexOf('://');
  const searchFrom = schemeEnd === -1 ? 0 : schemeEnd + 3;
  const slashIndex = text.indexOf('/', searchFrom);
  const authority = slashIndex === -1 ? text : text.slice(0, slashIndex);

  return authority.includes('{{')
    ? 'A placeholder cannot sit in the scheme, host, port or userinfo; move it into the path, query string or body.'
    : null;
};

export const toolErrors = (toolForm, { hasIdentityPlaceholder = false } = {}) => {
  const errors = {};
  const name = toolForm.name.trim();

  if (!TOOL_NAME_PATTERN.test(name))
    errors.name = 'Lowercase letters, digits and underscores, starting with a letter, 3 to 41 characters.';

  if (!toolForm.description.trim()) errors.description = 'A description is required.';
  else if (toolForm.description.length > CAPS.description) errors.description = `At most ${CAPS.description} characters.`;

  if (toolForm.guidance.length > CAPS.guidance) errors.guidance = `At most ${CAPS.guidance} characters.`;
  if (toolForm.notFoundText.length > CAPS.notFoundText) errors.notFoundText = `At most ${CAPS.notFoundText} characters.`;
  if (!inBounds(toolForm.maxRows, MAX_ROWS_BOUNDS))
    errors.maxRows = `Between ${MAX_ROWS_BOUNDS.min} and ${MAX_ROWS_BOUNDS.max}.`;
  if (toolForm.maxTextChars !== '' && !inBounds(toolForm.maxTextChars, TEXT_CHARS_BOUNDS))
    errors.maxTextChars = `Between ${TEXT_CHARS_BOUNDS.min} and ${TEXT_CHARS_BOUNDS.max}.`;
  // The server writes it raw inside <state>…</state>, so angle brackets are refused there too.
  const statusText = toolForm.statusText.trim();
  if (statusText.length > CAPS.statusText) errors.statusText = `At most ${CAPS.statusText} characters.`;
  else if (/[<>]/.test(statusText)) errors.statusText = 'No < or >: it is shown as plain text.';
  if (toolForm.argRows.length > MAX_ARGS) errors.argCount = `At most ${MAX_ARGS} arguments.`;

  const rows = toolForm.argRows.map((row) => oneArgErrors(row, toolForm.personal));
  if (rows.some((row) => Object.keys(row).length)) errors.argRows = rows;

  // {{identity.externalId}} does not count: the caller resolves which placeholders the server can
  // actually fill, and one that renders empty leaves a personal tool keyed on nothing.
  if (toolForm.personal && !hasIdentityPlaceholder)
    errors.personal =
      'A personal tool needs an {{identity.*}} placeholder the server can fill in — email, phone or profileId — in the URL or body.';

  return errors;
};
